import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '40px 24px', fontFamily: 'sans-serif', lineHeight: 1.7, color: '#1a1a2e' }}>
      <h1>Privacy Policy</h1>
      <p><strong>Effective Date:</strong> August 1, 2026</p>
      <p>
        This Privacy Policy describes how ChronoFlow ("we", "us", or "our"), based in San Francisco, California,
        collects, uses, and shares information about you when you use our AI-powered recruitment scheduling
        platform ("Service") at <a href="https://chronoflow.ai">chronoflow.ai</a>.
      </p>

      <h2>1. Information We Collect</h2>

      <h3>1.1 Information You Provide</h3>
      <ul>
        <li>Name and email address</li>
        <li>Job title and company name</li>
        <li>Profile information (resume, photo, salary expectations)</li>
        <li>Account credentials if using email/OTP login</li>
      </ul>

      <h3>1.2 Information from Google Sign-In and Google Calendar</h3>
      <p>
        When you sign in with Google, we request your consent to access the following information:
      </p>
      <ul>
        <li>Your name, email address, and profile photo (for account creation and identification)</li>
        <li>Your Google Calendar data — specifically, your availability slots and calendar events — solely for the purpose of scheduling and coordinating interviews within the Service</li>
      </ul>
      <p>
        <strong>Google API Limited Use Disclosure:</strong> ChronoFlow's use of information received from Google
        APIs adheres to the{' '}
        <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">
          Google API Services User Data Policy
        </a>
        , including the Limited Use requirements. We access your Google Calendar data only to display your
        availability to authorised parties (such as recruiters or hiring managers) and to create interview
        events on your calendar. We do not use Google user data for advertising, profiling, data resale, or
        any purpose beyond the core scheduling functionality described here.
      </p>

      <h3>1.3 Information from Microsoft Sign-In and Microsoft Calendar</h3>
      <p>
        When you sign in with Microsoft, we similarly request access to your name, email, profile photo, and
        Microsoft Calendar data for the same interview scheduling purposes.
      </p>

      <h3>1.4 Information Collected Automatically</h3>
      <ul>
        <li>Usage data (pages visited, features used)</li>
        <li>Device and browser information</li>
        <li>Log data (IP address, access times)</li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <ul>
        <li>To authenticate you and maintain your account</li>
        <li>To display your calendar availability and schedule interviews</li>
        <li>To match candidates with job opportunities</li>
        <li>To send notifications about application status and scheduled interviews</li>
        <li>To provide AI-powered candidate screening features</li>
        <li>To process payments for subscription plans</li>
        <li>To improve and personalise the Service</li>
        <li>To comply with legal obligations</li>
      </ul>
      <p>
        We do <strong>not</strong> sell, rent, or share your Google or Microsoft account data with third parties
        for advertising or any purpose unrelated to the core recruitment scheduling functionality described above.
      </p>

      <h2>3. Data Sharing and Disclosure</h2>
      <p>We share your information only in the following limited circumstances:</p>
      <ul>
        <li><strong>Within the Service:</strong> Your availability and profile may be visible to other authorised users involved in your recruitment process (e.g. a recruiter viewing a candidate's available interview slots).</li>
        <li><strong>Service providers:</strong> We use third-party providers to operate the Service, including AWS (file storage), Stripe (payments), Amazon SES (email), and Azure OpenAI (AI screening). These providers are bound by confidentiality obligations and may not use your data for their own purposes.</li>
        <li><strong>Legal requirements:</strong> We may disclose your information to comply with applicable laws or respond to valid legal process.</li>
        <li><strong>Protection of rights:</strong> We may disclose information to protect the rights, property, or safety of ChronoFlow, our users, or the public.</li>
        <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred with appropriate notice.</li>
      </ul>

      <h2>4. Data Security</h2>
      <p>
        We implement industry-standard technical and organisational security measures to protect your personal
        information. OAuth tokens from Google and Microsoft are stored securely and are never exposed to other
        users of the Service.
      </p>

      <h2>5. Data Retention</h2>
      <p>
        We retain your personal information for as long as your account is active or as needed to provide the
        Service. You may request deletion of your account and associated data at any time by contacting us at{' '}
        <a href="mailto:ammara@chronoflow.ai">ammara@chronoflow.ai</a>.
      </p>

      <h2>6. Your Rights and Choices</h2>
      <ul>
        <li><strong>Access and update:</strong> You can view and edit your profile information within the Service.</li>
        <li><strong>Delete your account:</strong> Contact us to request deletion of your data.</li>
        <li>
          <strong>Revoke Google access:</strong> You can revoke ChronoFlow's access to your Google account at
          any time via{' '}
          <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer">
            Google Account Permissions
          </a>. Revoking access will disable Google Calendar features in the Service.
        </li>
        <li><strong>Revoke Microsoft access:</strong> You can revoke access via your Microsoft account settings.</li>
        <li><strong>Opt-out of communications:</strong> You can manage notification preferences within the Service.</li>
        <li><strong>California residents:</strong> Under the California Consumer Privacy Act (CCPA), you have the right to know what personal information we collect, request deletion of your data, and opt out of the sale of your data. We do not sell personal information.</li>
      </ul>

      <h2>7. Third-Party Services</h2>
      <p>The Service integrates with the following third-party services, each governed by their own privacy policies:</p>
      <ul>
        <li><strong>Google</strong> — OAuth 2.0 sign-in and Google Calendar API</li>
        <li><strong>Microsoft</strong> — Azure AD sign-in and Microsoft Graph (Calendar) API</li>
        <li><strong>Stripe</strong> — Payment processing</li>
        <li><strong>Amazon Web Services (S3, SES)</strong> — File storage and transactional email</li>
        <li><strong>Azure OpenAI</strong> — AI-powered candidate screening</li>
      </ul>

      <h2>8. Children's Privacy</h2>
      <p>
        The Service is intended for professionals aged 18 and above. We do not knowingly collect personal
        information from anyone under 18. If you believe we have inadvertently collected such information,
        please contact us immediately.
      </p>

      <h2>9. Changes to this Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of material changes by email
        or by posting a notice within the Service. The effective date at the top of this page will be updated
        accordingly. Continued use of the Service after changes constitutes acceptance of the updated policy.
      </p>

      <h2>10. Contact Us</h2>
      <p>
        If you have questions or concerns about this Privacy Policy or how we handle your data, please contact us:
      </p>
      <p>
        <strong>ChronoFlow</strong><br />
        San Francisco, California, USA<br />
        <a href="mailto:ammara@chronoflow.ai">ammara@chronoflow.ai</a><br />
        <a href="https://chronoflow.ai">https://chronoflow.ai</a>
      </p>
    </div>
  );
};

export default PrivacyPolicy;
